import { Component, HostListener, OnInit } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { showSmartCreatePaidBadge } from "src/app/shared/utils/plan.util";

export type CreateFlowVideoStyle = "full-video" | "step-videos";
export type CreateFlowContentStyle = "manual" | "ai";

export type CreateFlowSetupResult =
  | { cancelled: true; navigateTo?: "home" | "selection" }
  | {
      cancelled: false;
      videoStyle: CreateFlowVideoStyle;
      contentStyle?: CreateFlowContentStyle;
      mode: "full-video-ai" | "pieces-manual" | "pieces-ai";
    };

@Component({
  selector: "app-create-flow-setup-dialog",
  templateUrl: "./create-flow-setup-dialog.component.html",
  styleUrls: ["./create-flow-setup-dialog.component.scss"],
})
export class CreateFlowSetupDialogComponent implements OnInit {
  step: 1 | 2 = 1;
  isMobileView = false;
  showSmartCreatePaidBadge = false;
  selectedVideoStyle: CreateFlowVideoStyle | null = null;
  selectedContentStyle: CreateFlowContentStyle | null = null;

  readonly videoOptions: Array<{
    id: CreateFlowVideoStyle;
    icon: string;
    title: string;
    subtitle: string;
    hint: string;
  }> = [
    {
      id: "full-video",
      icon: "movie",
      title: "One full tutorial video",
      subtitle: "A single recording that covers the whole process",
      hint: "AI splits the video into steps and writes titles, items, and instructions",
    },
    {
      id: "step-videos",
      icon: "video_library",
      title: "Separate video per step",
      subtitle: "You already recorded each step as its own clip",
      hint: "Upload your clips — choose manual captions or AI-generated text",
    },
  ];

  readonly contentOptions: Array<{
    id: CreateFlowContentStyle;
    icon: string;
    title: string;
    subtitle: string;
  }> = [
    {
      id: "manual",
      icon: "edit_note",
      title: "I'll write the content",
      subtitle: "Upload videos and add titles & descriptions yourself",
    },
    {
      id: "ai",
      icon: "auto_awesome",
      title: "AI writes the content",
      subtitle: "AI generates step titles, instructions, and flow details from your videos",
    },
  ];

  constructor(public dialogRef: MatDialogRef<CreateFlowSetupDialogComponent>) {}

  ngOnInit(): void {
    this.checkMobileView();
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    this.showSmartCreatePaidBadge = showSmartCreatePaidBadge(userData);
  }

  @HostListener("window:resize")
  onResize(): void {
    this.checkMobileView();
  }

  private checkMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;
  }

  cancelToSelection(): void {
    this.dialogRef.close({ cancelled: true, navigateTo: "selection" } as CreateFlowSetupResult);
  }

  selectVideoStyle(style: CreateFlowVideoStyle): void {
    this.selectedVideoStyle = style;
    if (style === "full-video") {
      this.finishSetup("full-video-ai");
      return;
    }
    this.step = 2;
  }

  selectContentStyle(style: CreateFlowContentStyle): void {
    this.selectedContentStyle = style;
    this.finishSetup(style === "ai" ? "pieces-ai" : "pieces-manual");
  }

  goBack(): void {
    if (this.step === 2) {
      this.step = 1;
      this.selectedContentStyle = null;
      return;
    }
    this.cancelToSelection();
  }

  private finishSetup(
    mode: "full-video-ai" | "pieces-manual" | "pieces-ai"
  ): void {
    this.dialogRef.close({
      cancelled: false as const,
      videoStyle: this.selectedVideoStyle!,
      contentStyle:
        this.selectedVideoStyle === "step-videos"
          ? this.selectedContentStyle ?? undefined
          : undefined,
      mode,
    });
  }
}
